import { useEffect, useRef, useState } from "react";
import { Toast } from "antd-mobile";
import { sendVerificationCode } from "../utils/auth";
import { t } from "../i18n";
import { validatePhoneNumber } from "../utils/phoneValidation";

interface GetPhoneResult {
  phone: string;
  countryCode: string;
}

interface SendVerificationCodeOptions {
  /** 验证码类型（login/register/change_password 等） */
  type: string;
  /** 获取手机号/区号信息 */
  getPhone: () => GetPhoneResult;
  /** 账号不存在时的回调（如弹出去注册提示） */
  onNotExist?: () => void;
  /** 账号被禁用时的回调 */
  onDisabled?: () => void;
  /** 账号已存在时的回调（如注册场景提示已注册） */
  onAccountExists?: () => void;
  /**
   * 自定义 messageKey 处理，返回 true 表示已处理
   */
  onCustomMessageKey?: (messageKey: string) => boolean;
}

export function useSendVerificationCode(options: SendVerificationCodeOptions) {
  const { type, getPhone, onNotExist, onDisabled, onAccountExists, onCustomMessageKey } = options;
  const [countdown, setCountdown] = useState(0); // 显示的倒计时数字（60, 59, 58...）
  const timerRef = useRef<NodeJS.Timeout | null>(null);   // 存储定时器ID，用于清除定时器
  const startTimeRef = useRef<number | null>(null);   // 存储倒计时开始的时间戳
  const countdownDuration = 60; // 倒计时总时长（秒）

  // 存储更新函数，避免闭包问题
  const updateCountdownRef = useRef<() => void>();

  // 计算剩余倒计时时间
  const calculateRemainingTime = (): number => {
    if (startTimeRef.current === null) {
      return 0;
    }
    const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
    const remaining = Math.max(0, countdownDuration - elapsed);
    return remaining;
  };

  // 更新倒计时显示
  const updateCountdown = () => {
    const remaining = calculateRemainingTime();
    setCountdown(remaining); // 更新UI显示

    if (remaining <= 0) {
      // 如果倒计时结束，清理定时器和开始时间
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      startTimeRef.current = null;
    }
  };

  // 将更新函数存储到 ref，确保总是访问最新版本
  updateCountdownRef.current = updateCountdown;

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && startTimeRef.current !== null) {
        // 页面从后台切回前台时，立即更新倒计时
        updateCountdownRef.current?.(); // 通过ref访问最新函数
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  const startCountdown = () => {
    startTimeRef.current = Date.now(); // 记录开始时间戳
    setCountdown(countdownDuration); // 立即显示60秒

    // 清除之前的定时器
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    // 每秒执行一次更新
    timerRef.current = setInterval(() => {
      updateCountdownRef.current?.();
    }, 1000);
  };

  const sendCode = async () => {
    const { phone, countryCode } = getPhone();

    // 使用手机号校验工具
    const phoneValidation = validatePhoneNumber(phone, countryCode);
    if (!phoneValidation.isValid) {
      Toast.show(phoneValidation.errorMessage!);
      return;
    }

    // UI 防抖，实际频控由后端处理
    if (countdown > 0) {
      Toast.show(t("common.verificationCode.wait"));
      return;
    }

    try {
      const result = await sendVerificationCode(
        Number(countryCode.replace("+", "")),
        phoneValidation.phoneDigits!,
        type
      );

      if (result.success) {
        Toast.show(t("common.verificationCode.sent"));
        startCountdown();
        return;
      }

      const messageKey = result.messageKey;
      // 自定义处理
      if (messageKey && onCustomMessageKey && onCustomMessageKey(messageKey)) {
        return;
      }

      if (messageKey === "errors.business.account.verificationCode.wait") {
        Toast.show(t("common.verificationCode.wait"));
        return;
      }

      if (messageKey === "errors.business.account.verificationCode.sendFailed") {
        Toast.show(t("common.verificationCode.sendFailed"));
        return;
      }

      if (messageKey === "errors.business.account.verificationCode.invalid") {
        Toast.show(t("common.verificationCode.codeInvalid"));
        return;
      }

      if (messageKey === "errors.business.account.verificationCode.notAllowedForRole") {
        Toast.show(t("errors.business.account.verificationCode.notAllowedForRole"));
        return;
      }

      if (messageKey === "errors.business.account.verificationCode.notAllowedForRoleChangePassword") {
        Toast.show(t("errors.business.account.verificationCode.notAllowedForRoleChangePassword"));
        return;
      }

      if (messageKey === "errors.business.account.notExist") {
        onNotExist?.();
        return;
      }

      if (messageKey === "errors.business.account.disabled") {
        onDisabled?.();
        return;
      }

      if (messageKey === "errors.business.account.register.phoneExists") {
        onAccountExists?.();
        return;
      }

      const message = messageKey ? t(messageKey) : t("common.verificationCode.wait");
      Toast.show(message);
    } catch (error: any) {
      const messageKey = error?.graphQLErrors?.[0]?.extensions?.messageKey;
      const message = messageKey ? t(messageKey) : t("common.verificationCode.sendFailed");
      Toast.show(message);
    }
  };

  return { sendCode, countdown };
}

